import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { userAPI } from '../../services/api';
import './style.css';

export default function Signup({ onComplete }) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    parentEmail: '',
    coachEmail: '',
    school: '',
    birthDate: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Required field validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.birthDate) {
      newErrors.birthDate = 'Birth date is required';
    } else {
      // Check if age is reasonable (between 5 and 100 years old)
      const birthDate = new Date(formData.birthDate);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      
      if (age < 5 || age > 100) {
        newErrors.birthDate = 'Please enter a valid birth date';
      }
    }
    
    // Optional email validation (only validate if filled)
    if (formData.parentEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.parentEmail)) {
      newErrors.parentEmail = 'Please enter a valid email address';
    }
    
    if (formData.coachEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.coachEmail)) {
      newErrors.coachEmail = 'Please enter a valid email address';
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Call the backend API to create user
      const response = await userAPI.signup(formData);
      
      if (response.success) {
        // Store user data in localStorage
        localStorage.setItem('currentUser', JSON.stringify(response.user));
        localStorage.setItem('userId', response.user.user_id);
        
        console.log('User signed up:', response.user);
        
        // Call the completion callback if provided
        if (onComplete) {
          onComplete();
        } else {
          // Fallback for when Signup is used standalone
          window.location.href = '/cognitive-assessment';
        }
      } else {
        throw new Error(response.error || 'Signup failed');
      }
    } catch (error) {
      console.error('Signup error:', error);
      setErrors({ submit: 'An error occurred. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="signup-container">
      <div className="signup-wrap">
        <h1 className="signup-title">Native Brilliance Assessment Signup</h1>
        <p className="signup-description">Sign up here to discover your Native Brilliance.</p>
        
        <form className="signup-form" onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">First Name *</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className={errors.firstName ? 'error' : ''}
                disabled={isSubmitting}
              />
              {errors.firstName && <span className="error-message">{errors.firstName}</span>}
            </div>
            
            <div className="form-group">
              <label htmlFor="lastName">Last Name *</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className={errors.lastName ? 'error' : ''}
                disabled={isSubmitting}
              />
              {errors.lastName && <span className="error-message">{errors.lastName}</span>}
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="email">Email Address *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={errors.email ? 'error' : ''}
              disabled={isSubmitting}
              placeholder="your@email.com"
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
            <span className="field-hint">This email will be used to access your results in the iOS app</span>
          </div>
          
          <div className="form-group">
            <label htmlFor="birthDate">Birth Date *</label>
            <input
              type="date"
              id="birthDate"
              name="birthDate"
              value={formData.birthDate}
              onChange={handleChange}
              className={errors.birthDate ? 'error' : ''}
              disabled={isSubmitting}
            />
            {errors.birthDate && <span className="error-message">{errors.birthDate}</span>}
          </div>
          
          <div className="form-group">
            <label htmlFor="parentEmail">Parent Email Address (Optional)</label>
            <input
              type="email"
              id="parentEmail"
              name="parentEmail"
              value={formData.parentEmail}
              onChange={handleChange}
              className={errors.parentEmail ? 'error' : ''}
              disabled={isSubmitting}
              placeholder="parent@email.com"
            />
            {errors.parentEmail && <span className="error-message">{errors.parentEmail}</span>}
          </div>
          
          <div className="form-group">
            <label htmlFor="coachEmail">Coach/Therapist Email Address (Optional)</label>
            <input
              type="email"
              id="coachEmail"
              name="coachEmail"
              value={formData.coachEmail}
              onChange={handleChange}
              className={errors.coachEmail ? 'error' : ''}
              disabled={isSubmitting}
              placeholder="coach@email.com"
            />
            {errors.coachEmail && <span className="error-message">{errors.coachEmail}</span>}
          </div>
          
          <div className="form-group">
            <label htmlFor="school">School (Optional)</label>
            <input
              type="text"
              id="school"
              name="school"
              value={formData.school}
              onChange={handleChange}
              disabled={isSubmitting}
              placeholder="Your school name"
            />
          </div>
          
          {errors.submit && (
            <div className="error-message submit-error">{errors.submit}</div>
          )}
          
          <button 
            type="submit" 
            className="signup-submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Processing...' : 'Start Assessment'}
          </button>
        </form>
      </div>
    </div>
  );
}